import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Chip,
  Autocomplete,
  TextField,
  Typography,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  LocalOffer as TagIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import api from '../api';

const TagSelector = ({ 
  selectedTagIds = [], 
  onTagsChange, 
  label = "Tags",
  placeholder = "Select tags...",
  disabled = false,
  error = null,
  currentLanguage = 'english'
}) => {
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchTags();
  }, [currentLanguage]); // Refetch when language changes

  const fetchTags = async () => {
    try {
      setLoading(true);
      // Pass language parameter to backend for localized tag names
      const response = await api.get(`/tags?language=${currentLanguage}`);
      setTags(response.data.data || []);
      setFetchError(null);
    } catch (err) {
      setFetchError('Failed to fetch tags');
      console.error('Error fetching tags:', err);
    } finally {
      setLoading(false);
    }
  };

  const selectedTags = tags.filter(tag => selectedTagIds.includes(tag.id));

  // Helper function to get tag name for current language
  const getTagName = useCallback((tag) => {
    // If backend provided localized name directly, use it
    if (tag.localizedName) {
      return tag.localizedName;
    }
    // Fallback to languageSpec if available
    if (tag.languageSpec && tag.languageSpec[currentLanguage] && tag.languageSpec[currentLanguage].name) {
      return tag.languageSpec[currentLanguage].name;
    }
    // Final fallback to original name
    return tag.name || '';
  }, [currentLanguage]);

  // Helper function to get full tag display with emoji
  const getTagDisplay = useCallback((tag) => {
    const name = getTagName(tag);
    return tag.emoji ? `${tag.emoji} ${name}` : name;
  }, [getTagName]);

  // Filter tags based on search term
  const filteredTags = useMemo(() => {
    if (!searchTerm.trim()) {
      return tags;
    }
    
    return tags.filter(tag => {
      const tagName = getTagName(tag).toLowerCase();
      const search = searchTerm.toLowerCase();
      return tagName.includes(search) || (tag.emoji && tag.emoji.includes(search));
    });
  }, [tags, searchTerm, getTagName]);


  const getContrastColor = (hex) => {
    if (!hex) return '#000000';
    // Convert hex to RGB
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    
    // Calculate luminance
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    
    return luminance > 0.5 ? '#000000' : '#FFFFFF';
  };

  const handleTagsChange = (_, newTags) => {
    const newTagIds = newTags.map(tag => tag.id);
    onTagsChange(newTagIds);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
        <CircularProgress size={20} />
        <Typography variant="body2" color="textSecondary">
          Loading tags...
        </Typography>
      </Box>
    );
  }

  if (fetchError) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {fetchError}
      </Alert>
    );
  }

  return (
    <Box>
      <Autocomplete
        multiple
        disabled={disabled}
        options={filteredTags}
        getOptionLabel={(option) => getTagDisplay(option)}
        value={selectedTags}
        onChange={handleTagsChange}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        onInputChange={(_, newInputValue) => {
          setSearchTerm(newInputValue);
        }}
        inputValue={searchTerm}
        clearOnEscape
        clearOnBlur={false}
        selectOnFocus
        handleHomeEndKeys
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip
              {...getTagProps({ index })}
              key={option.id}
              label={getTagDisplay(option)}
              size="small"
              sx={{
                backgroundColor: option.color,
                color: getContrastColor(option.color),
                fontWeight: 'bold',
                '& .MuiChip-deleteIcon': {
                  color: getContrastColor(option.color),
                  '&:hover': {
                    color: getContrastColor(option.color),
                    opacity: 0.7
                  }
                }
              }}
            />
          ))
        }
        renderOption={(props, option) => (
          <li 
            {...props} 
            style={{ 
              listStyle: 'none', 
              padding: '12px 16px', 
              margin: 0,
              cursor: 'pointer',
              transition: 'background-color 0.2s ease'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
              <Chip
                label={getTagDisplay(option)}
                size="small"
                sx={{
                  backgroundColor: option.color,
                  color: getContrastColor(option.color),
                  fontWeight: 'bold',
                  fontSize: '0.8125rem',
                  height: '24px'
                }}
              />
            </Box>
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder={selectedTags.length === 0 ? "Search and select tags..." : "Search more tags..."}
            error={!!error}
            helperText={error}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <>
                  <TagIcon sx={{ color: 'action.active', mr: 1 }} />
                  {params.InputProps.startAdornment}
                </>
              ),
            }}
            sx={{
              minWidth: 500,
              '& .MuiInputBase-input': {
                cursor: 'text !important',
                minHeight: '20px'
              },
              '& .MuiInputBase-root': {
                minHeight: '56px'
              }
            }}
          />
        )}
        slotProps={{
          paper: {
            sx: { 
              maxHeight: 320, 
              minHeight: 160,
              overflow: 'auto', 
              minWidth: 500 
            }
          },
          listbox: {
            style: { 
              listStyle: 'none', 
              padding: '8px 0', 
              margin: 0
            }
          }
        }}
        slots={{
          listbox: ({ children, ...other }) => (
            <ul {...other}>
              {filteredTags.length === 0 && searchTerm ? (
                <li style={{ padding: '32px 16px', textAlign: 'center', color: '#666', listStyle: 'none' }}>
                  <SearchIcon sx={{ mb: 1, fontSize: 24, color: '#999' }} />
                  <Typography variant="body2" sx={{ color: '#666' }}>
                    No tags found for "{searchTerm}"
                  </Typography>
                </li>
              ) : (
                children
              )}
            </ul>
          )
        }}
        filterOptions={(options) => options}
      />
      
      {selectedTags.length > 0 && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="body2" color="textSecondary">
            Selected tags:
          </Typography>
          <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {selectedTags.map((tag) => (
              <Chip
                key={tag.id}
                label={getTagDisplay(tag)}
                size="small"
                variant="outlined"
                sx={{
                  borderColor: tag.color,
                  color: tag.color,
                  backgroundColor: `${tag.color}15`,
                  fontWeight: 'bold'
                }}
              />
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default TagSelector;